import * as React from "react"

import Router from "./components/Layout/Router"
import SessionStore from "./stores/sessionStore"
import SignalRAspNetCoreHelper from "./lib/signalRAspNetCoreHelper"
import Stores from "./stores/storeIdentifier"
import { inject, observer } from "mobx-react"
import withSplashScreen from "@components/Layout/SplashScreen"
import SplashScreen from "@components/SplashScreen"
import {
  shouldShowSplash,
  markSplashComplete,
} from "@components/SplashScreen/utils"

import { HelmetProvider } from "react-helmet-async"

import "./App.less"

export interface IAppProps {
  sessionStore?: SessionStore
}

@inject(Stores.SessionStore)
@observer
class App extends React.Component<IAppProps> {
  state = {
    isLoading: true,
    showSplash: false,
    appReady: false,
    loadError: false,
  }

  async componentDidMount() {
    await this.loadAppData()
  }

  loadAppData = async () => {
    try {
      await this.props.sessionStore!.getCurrentLoginInformations()

      if (
        !!this.props.sessionStore!.currentLogin?.user &&
        this.props.sessionStore!.currentLogin?.application?.features?.[
          "SignalR"
        ]
      ) {
        SignalRAspNetCoreHelper.initSignalR()
      }

      if (!!this.props.sessionStore!.currentLogin?.user) {
        await Promise.all([
          this.props.sessionStore?.getMyProfilePicture(),
          this.props.sessionStore!.getOwnProjects({}),
        ])
      }

      // App data loaded: signal readiness so the splash can fade out, and
      // clear any prior load error (Requirements 2.6, 8.2).
      this.setState({
        isLoading: false,
        showSplash: shouldShowSplash(),
        appReady: true,
        loadError: false,
      })
    } catch (error) {
      console.error("Error in componentDidMount:", error)
      // Đảm bảo UI không bị treo nếu lỗi xảy ra; không hiển thị splash khi lỗi.
      // Đánh dấu lỗi tải để hiển thị thông báo kèm tùy chọn thử lại (Req 2.7).
      this.setState({
        isLoading: false,
        showSplash: false,
        appReady: false,
        loadError: true,
      })
    }
  }

  handleSplashComplete = () => {
    markSplashComplete()
    this.setState({ showSplash: false })
  }

  // Readiness gate timed out: app data never became ready within the splash's
  // max wait window. Surface an error state with a retry option (Req 2.7).
  handleReadyTimeout = () => {
    this.setState({ loadError: true })
  }

  handleRetry = () => {
    this.setState({ isLoading: true, loadError: false, appReady: false })
    this.loadAppData()
  }

  public render() {
    if (this.state.isLoading) return null

    return (
      <HelmetProvider>
        {this.state.showSplash && (
          <SplashScreen
            onComplete={this.handleSplashComplete}
            duration={2500}
            skipable={true}
            isAppReady={this.state.appReady}
            onReadyTimeout={this.handleReadyTimeout}
          />
        )}
        {this.state.loadError && !this.state.showSplash && (
          <div role="alert" className="app-load-error-overlay">
            <div className="app-load-error-card">
              <h2 className="app-load-error-title">Không thể tải ứng dụng</h2>
              <p className="app-load-error-message">
                Đã xảy ra lỗi khi tải dữ liệu ứng dụng. Vui lòng thử lại.
              </p>
              <button
                type="button"
                className="app-load-error-retry"
                onClick={this.handleRetry}
              >
                Thử lại
              </button>
            </div>
          </div>
        )}
        <Router />
      </HelmetProvider>
    )
  }
}

export default withSplashScreen(App)
